import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// Couleur de ceinture (pastille) — choisit la couleur la plus avancée d'un nom composé.
function publicBeltHex(belt) {
  const b = (belt || "").toLowerCase();
  const order = [
    ["noir", "#111827"], ["brun", "#78350f"], ["mauve", "#a855f7"], ["bleu", "#3b82f6"],
    ["vert", "#22c55e"], ["orange", "#fb923c"], ["rouge", "#ef4444"], ["jaune", "#facc15"], ["blanc", "#f4f4f5"],
  ];
  for (const [k, hex] of order) { if (b.includes(k)) return hex; }
  return "#71717a";
}

const STATUS_LABEL = {
  a_decouvrir: "À découvrir",
  en_apprentissage: "En apprentissage",
  en_consolidation: "En consolidation",
  prete_a_evaluer: "Prête à évaluer",
  validee: "Validée",
};

const STATUS_COLOR = {
  a_decouvrir: "bg-zinc-700 text-zinc-300",
  en_apprentissage: "bg-blue-900/60 text-blue-200",
  en_consolidation: "bg-amber-900/60 text-amber-200",
  prete_a_evaluer: "bg-emerald-900/60 text-emerald-200",
  validee: "bg-green-700 text-green-50",
};

// Boutons d'auto-évaluation (Petits Samouraïs = étoiles, autres = %)
const PERCENT_OPTIONS_KIDS = [
  { v: 0, label: "Pas encore", emoji: "🥚" },
  { v: 50, label: "J'apprends", emoji: "⭐" },
  { v: 75, label: "Je la connais", emoji: "⭐⭐" },
  { v: 100, label: "Je la maîtrise !", emoji: "⭐⭐⭐" },
];
const PERCENT_OPTIONS = [
  { v: 0, label: "Pas encore", color: "bg-zinc-700 hover:bg-zinc-600 text-zinc-200" },
  { v: 25, label: "Je découvre", color: "bg-blue-900/70 hover:bg-blue-800 text-blue-100" },
  { v: 50, label: "J'apprends", color: "bg-indigo-900/70 hover:bg-indigo-800 text-indigo-100" },
  { v: 75, label: "Je la connais", color: "bg-amber-900/70 hover:bg-amber-800 text-amber-100" },
  { v: 100, label: "Je la maîtrise", color: "bg-emerald-800 hover:bg-emerald-700 text-emerald-50" },
];

const INTENSITY_LABEL = { light: "Léger", moderate: "Modéré", intense: "Intense" };
const FOCUS_LABEL = {
  warmup: "Échauffement", mechanics: "Mécanique", shadow: "Shadow", combination: "Enchaînement",
  bag_pads: "Sac / Paos", drill: "Drill", validation: "Validation", cooldown: "Récupération",
};
function fmtTime(s) {
  const m = Math.floor(s / 60), sec = s % 60;
  return `${m}:${String(sec).padStart(2, "0")}`;
}

// Lecteur guidé plein écran avec minuteur — enchaîne les blocs de la séance.
function TrainingPlayer({ tr, onClose }) {
  const blocks = (tr.sessionBlocks || []).filter((b) => (b.durationMinutes || 0) > 0 || (b.instructions || []).length);
  const [idx, setIdx] = useState(0);
  const [remaining, setRemaining] = useState(() => Math.max(1, (blocks[0]?.durationMinutes || 1) * 60));
  const [paused, setPaused] = useState(false);
  const [done, setDone] = useState(false);
  const [muted, setMuted] = useState(false);
  const audioRef = useRef(null);

  const beep = (freq = 880, ms = 120) => {
    if (muted) return;
    try {
      if (!audioRef.current) audioRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const ctx = audioRef.current;
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.type = "sine"; o.frequency.value = freq;
      o.connect(g); g.connect(ctx.destination);
      g.gain.setValueAtTime(0.001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.01);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + ms / 1000);
      o.start(); o.stop(ctx.currentTime + ms / 1000);
    } catch (e) { /* audio non dispo */ }
  };

  const block = blocks[idx];

  useEffect(() => {
    setRemaining(Math.max(1, (blocks[idx]?.durationMinutes || 1) * 60));
    setPaused(false);
    if (idx > 0) beep(1046, 200); // bip de passage au bloc suivant
  }, [idx]); // eslint-disable-line

  useEffect(() => {
    if (paused || done) return;
    if (remaining <= 3 && remaining > 0) beep(880, 110);
  }, [remaining, paused, done]); // eslint-disable-line

  useEffect(() => {
    if (done) beep(660, 450);
  }, [done]); // eslint-disable-line

  useEffect(() => {
    if (paused || done) return;
    const t = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          clearInterval(t);
          if (idx < blocks.length - 1) setIdx((i) => i + 1);
          else setDone(true);
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [paused, done, idx, blocks.length]);

  const totalSecs = blocks.reduce((a, b) => a + Math.max(1, (b.durationMinutes || 1) * 60), 0);
  const elapsedBefore = blocks.slice(0, idx).reduce((a, b) => a + Math.max(1, (b.durationMinutes || 1) * 60), 0);
  const globalPct = Math.min(100, Math.round(((elapsedBefore + ((Math.max(1, (block?.durationMinutes || 1) * 60)) - remaining)) / totalSecs) * 100));

  if (!block) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-[#0a0a0a] flex flex-col" data-testid="training-player">
      <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-amber-400/80">🥋 {tr.title}</div>
          <div className="text-white/50 text-xs">Bloc {idx + 1} / {blocks.length}</div>
        </div>
        <button onClick={onClose} data-testid="training-player-close" className="text-white/60 hover:text-white text-2xl leading-none px-2">×</button>
      </div>

      <div className="absolute top-4 right-14">
        <button onClick={() => setMuted((m) => !m)} data-testid="training-player-mute"
          className="text-white/50 hover:text-white text-lg px-2" title={muted ? "Activer le son" : "Couper le son"}>
          {muted ? "🔇" : "🔊"}
        </button>
      </div>

      <div className="h-1 bg-white/10"><div className="h-full bg-amber-500 transition-all duration-1000" style={{ width: `${globalPct}%` }} /></div>

      {done ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center px-6" data-testid="training-player-done">
          <div className="text-6xl mb-4">🎉</div>
          <h2 className="text-2xl font-black text-amber-400">Entraînement terminé !</h2>
          <p className="text-white/60 mt-2 max-w-md">Bravo — tu as complété « {tr.title} ». Un entraînement à la fois, c'est ça l'esprit Kaizen.</p>
          <button onClick={onClose} className="mt-8 bg-amber-500 hover:bg-amber-400 text-black font-bold px-8 py-3 rounded-md">Fermer</button>
        </div>
      ) : (
        <>
          <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
            <div className="text-amber-300 text-lg font-bold mb-1">{block.title}</div>
            {block.focus && <div className="text-white/40 text-xs uppercase tracking-widest mb-6">{FOCUS_LABEL[block.focus] || block.focus}</div>}
            <div className={`text-7xl sm:text-8xl font-black tabular-nums mb-6 ${remaining <= 5 ? "text-red-500" : "text-white"}`} data-testid="training-player-timer">
              {fmtTime(remaining)}
            </div>
            {(block.instructions || []).length > 0 && (
              <ul className="max-w-md space-y-1.5 text-left">
                {block.instructions.map((ins, k) => (
                  <li key={k} className="text-sm text-white/70 flex gap-2"><span className="text-amber-400">•</span>{ins}</li>
                ))}
              </ul>
            )}
            {block.rounds ? <div className="mt-4 text-xs text-amber-200/70">{block.rounds} rounds</div> : null}
          </div>

          <div className="flex items-center justify-center gap-3 px-5 py-6 border-t border-white/10">
            <button onClick={() => setIdx((i) => Math.max(0, i - 1))} disabled={idx === 0}
              className="px-4 py-2.5 rounded-md border border-white/15 text-white/70 disabled:opacity-30 hover:bg-white/5" data-testid="training-player-prev">⏮ Précédent</button>
            <button onClick={() => setPaused((p) => !p)} data-testid="training-player-pause"
              className="px-8 py-2.5 rounded-md bg-amber-500 hover:bg-amber-400 text-black font-bold">{paused ? "▶ Reprendre" : "⏸ Pause"}</button>
            <button onClick={() => { if (idx < blocks.length - 1) setIdx((i) => i + 1); else setDone(true); }}
              className="px-4 py-2.5 rounded-md border border-white/15 text-white/70 hover:bg-white/5" data-testid="training-player-next">Suivant ⏭</button>
          </div>
        </>
      )}
    </div>
  );
}



function TrainingCard({ tr }) {
  const [open, setOpen] = useState(false);
  const [playing, setPlaying] = useState(false);
  const blocks = tr.sessionBlocks || [];
  return (
    <div className="rounded-lg border border-amber-800/40 bg-amber-950/25 overflow-hidden" data-testid={`assoc-training-${tr.id}`}>
      {playing && <TrainingPlayer tr={tr} onClose={() => setPlaying(false)} />}
      <button
        onClick={() => setOpen(!open)}
        data-testid={`assoc-training-toggle-${tr.id}`}
        className="w-full text-left px-3 py-2.5 flex items-center justify-between gap-2 hover:bg-amber-900/20 transition"
      >
        <div className="min-w-0">
          <div className="text-[13px] font-semibold text-amber-100 truncate">🥋 {tr.title}</div>
          {tr.shortDescription && (
            <div className="text-[11px] text-amber-200/60 truncate">{tr.shortDescription}</div>
          )}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {tr.durationMinutes != null && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-black/40 border border-amber-800/50 text-amber-200">⏱ {tr.durationMinutes} min</span>
          )}
          {tr.estimatedIntensity && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-black/40 border border-amber-800/50 text-amber-200/80">{INTENSITY_LABEL[tr.estimatedIntensity] || tr.estimatedIntensity}</span>
          )}
          <span className="text-amber-300 text-xs">{open ? "▲" : "▼"}</span>
        </div>
      </button>
      {open && (
        <div className="px-3 pb-3 pt-1 space-y-3 border-t border-amber-800/30" data-testid={`assoc-training-detail-${tr.id}`}>
          {blocks.length > 0 && (
            <button
              onClick={() => setPlaying(true)}
              data-testid={`assoc-training-play-${tr.id}`}
              className="w-full flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 text-black font-bold text-sm py-2.5 rounded-md transition-colors"
            >
              ▶ Démarrer l'entraînement guidé{tr.durationMinutes ? ` · ${tr.durationMinutes} min` : ""}
            </button>
          )}
          {(tr.objectives || []).length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-amber-300/80 mb-1">Objectifs</div>
              <ul className="space-y-0.5">
                {tr.objectives.map((o, i) => (
                  <li key={i} className="text-[12px] text-amber-100/85 flex gap-1.5"><span className="text-amber-400">›</span>{o}</li>
                ))}
              </ul>
            </div>
          )}
          {blocks.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-amber-300/80 mb-1">Déroulé guidé</div>
              <ol className="space-y-1.5">
                {blocks.map((b, i) => (
                  <li key={i} className="rounded-md bg-black/30 border border-amber-900/30 px-2.5 py-2" data-testid={`assoc-block-${tr.id}-${i}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[12px] font-medium text-amber-100">{i + 1}. {b.title}</span>
                      <span className="text-[10px] text-amber-200/70 shrink-0">
                        {b.durationMinutes != null ? `${b.durationMinutes} min` : ""}
                        {b.rounds ? ` · ${b.rounds}×${b.roundDurationSeconds ? Math.round(b.roundDurationSeconds / 60 * 10) / 10 + "min" : ""}` : ""}
                      </span>
                    </div>
                    {b.focus && <span className="text-[10px] text-amber-300/60">{FOCUS_LABEL[b.focus] || b.focus}</span>}
                    {(b.instructions || []).length > 0 && (
                      <ul className="mt-1 space-y-0.5">
                        {b.instructions.map((ins, k) => (
                          <li key={k} className="text-[11px] text-amber-100/70">• {ins}</li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ol>
            </div>
          )}
          {tr.safetyNotice && (
            <div className="text-[11px] text-red-200/80 bg-red-950/30 border border-red-900/40 rounded-md px-2.5 py-1.5">
              🛡 <b>Sécurité :</b> {tr.safetyNotice}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function TechniqueRow({ tech, token, showPercent, isKid, onUpdated }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [localPct, setLocalPct] = useState(tech.self_reported_percent ?? tech.percent ?? 0);
  const [localStatus, setLocalStatus] = useState(tech.status);
  const [quizOpen, setQuizOpen] = useState(false);
  const [quizAns, setQuizAns] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const [quizBusy, setQuizBusy] = useState(false);
  const [quizPassed, setQuizPassed] = useState(tech.quiz_passed || false);
  const [videoOpen, setVideoOpen] = useState(false);
  const embedUrl = tech.video_embed_url || "";

  const hasQuiz = Array.isArray(tech.quiz) && tech.quiz.length > 0;

  const submitQuiz = async () => {
    setQuizBusy(true);
    try {
      const answers = (tech.quiz || []).map((_, i) => (quizAns[i] ?? null));
      const { data } = await axios.post(
        `${API}/api/public/carnet/${token}/quiz/${tech.id}/submit`,
        { answers }
      );
      setQuizResult(data);
      if (data.passed) setQuizPassed(true);
    } catch (e) {
      alert("Erreur quiz : " + (e.response?.data?.detail || "réessaie"));
    } finally {
      setQuizBusy(false);
    }
  };

  const submit = async (pct, requestValidation = false) => {
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/api/public/carnet/${token}/technique`,
        { technique_id: tech.id, self_percent: pct, request_validation: requestValidation }
      );
      setLocalPct(pct);
      setLocalStatus(data.new_status || localStatus);
      setSaved(true);
      onUpdated && onUpdated(tech.id, data);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      alert("Erreur : " + (e.response?.data?.detail || "réessaie plus tard"));
    } finally {
      setBusy(false);
    }
  };

  const options = isKid ? PERCENT_OPTIONS_KIDS : PERCENT_OPTIONS;
  const selfReported = tech.self_reported_percent !== null && tech.self_reported_percent !== undefined;

  return (
    <div className="rounded-lg bg-zinc-900/40 border border-zinc-800 p-3" data-testid={`tech-row-${tech.id}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-zinc-100">{tech.name}</div>
          {tech.belt_required && (
            <div className="text-xs text-zinc-400 inline-flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full border border-white/30 shrink-0" style={{ background: publicBeltHex(tech.belt_required) }} />
              Ceinture {tech.belt_required}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {showPercent && (
            <div className="text-sm font-mono text-emerald-300">{Math.round(localPct || tech.percent || 0)}%</div>
          )}
          <span className={`text-[10px] px-2 py-0.5 rounded-full ${STATUS_COLOR[localStatus] || "bg-zinc-800"}`}>
            {STATUS_LABEL[localStatus]}
          </span>
        </div>
      </div>

      {/* Trigger row */}
      <div className="mt-2 flex items-center gap-2">
        <button
          onClick={() => setOpen(!open)}
          className="text-[11px] px-2.5 py-1 rounded-md bg-amber-950/60 hover:bg-amber-900/70 border border-amber-800/60 text-amber-200 transition"
          data-testid={`btn-toggle-${tech.id}`}
        >
          {open ? "Fermer" : (selfReported ? "Modifier mon évaluation" : "M'auto-évaluer")}
        </button>
        {selfReported && !open && (
          <span className="text-[10px] text-zinc-500">
            Auto-éval : {tech.self_reported_percent}% {tech.coach_validated ? "✓ validé coach" : "(en attente coach)"}
          </span>
        )}
        {embedUrl && (
          <button
            onClick={() => setVideoOpen(true)}
            data-testid={`btn-video-${tech.id}`}
            className="text-[11px] px-2.5 py-1 rounded-md bg-red-950/60 hover:bg-red-900/70 border border-red-800/60 text-red-200 transition flex items-center gap-1"
          >
            ▶ Voir la vidéo
          </button>
        )}
        {hasQuiz && (
          <button
            onClick={() => { setQuizOpen(!quizOpen); setQuizResult(null); }}
            data-testid={`btn-quiz-${tech.id}`}
            className={`text-[11px] px-2.5 py-1 rounded-md border transition flex items-center gap-1 ${
              quizPassed
                ? "bg-emerald-950/60 border-emerald-700/60 text-emerald-300"
                : "bg-violet-950/60 hover:bg-violet-900/70 border-violet-800/60 text-violet-200"
            }`}
          >
            🧠 {quizPassed ? "Quiz réussi ✓" : (quizOpen ? "Fermer le quiz" : "Teste ta technique")}
          </button>
        )}
        {saved && <span className="text-[11px] text-emerald-400 animate-pulse">✓ Enregistré</span>}
      </div>

      {/* 🥋 Entraînements associés (guidés) — Feature #5 */}
      {(tech.associated_trainings || []).length > 0 && (
        <div className="mt-3 space-y-2" data-testid={`assoc-trainings-${tech.id}`}>
          <div className="text-[10px] uppercase tracking-wide text-amber-300/80">
            🥋 Entraînement{tech.associated_trainings.length > 1 ? "s" : ""} associé{tech.associated_trainings.length > 1 ? "s" : ""} · guidé{tech.associated_trainings.length > 1 ? "s" : ""}
          </div>
          {tech.associated_trainings.map((tr) => (
            <TrainingCard key={tr.id} tr={tr} />
          ))}
        </div>
      )}

      {quizOpen && hasQuiz && (
        <div className="mt-3 pt-3 border-t border-violet-900/40 space-y-4" data-testid={`quiz-panel-${tech.id}`}>
          <div className="text-[11px] text-violet-300 uppercase tracking-wider">🧠 Mini-quiz · teste ta compréhension</div>
          {tech.quiz.map((q, qi) => {
            const res = quizResult?.results?.[qi];
            return (
              <div key={qi} className="space-y-1.5">
                <div className="text-sm text-zinc-100 font-medium">{qi + 1}. {q.q}</div>
                <div className="grid gap-1.5">
                  {q.options.map((opt, oi) => {
                    const selected = quizAns[qi] === oi;
                    let cls = "bg-zinc-800/70 border-zinc-700 text-zinc-200 hover:border-violet-600";
                    if (quizResult) {
                      if (oi === res?.correct_index) cls = "bg-emerald-900/50 border-emerald-600 text-emerald-100";
                      else if (selected) cls = "bg-red-900/50 border-red-600 text-red-100";
                      else cls = "bg-zinc-800/40 border-zinc-800 text-zinc-400";
                    } else if (selected) {
                      cls = "bg-violet-900/50 border-violet-500 text-white";
                    }
                    return (
                      <button
                        key={oi}
                        disabled={!!quizResult}
                        onClick={() => setQuizAns({ ...quizAns, [qi]: oi })}
                        data-testid={`quiz-opt-${tech.id}-${qi}-${oi}`}
                        className={`text-left text-xs px-3 py-2 rounded-md border transition ${cls}`}
                      >
                        {opt}
                      </button>
                    );
                  })}
                </div>
                {quizResult && res?.explanation && (
                  <div className="text-[11px] text-zinc-400 italic pl-1">💡 {res.explanation}</div>
                )}
              </div>
            );
          })}
          {!quizResult ? (
            <button
              onClick={submitQuiz}
              disabled={quizBusy || Object.keys(quizAns).length < tech.quiz.length}
              data-testid={`quiz-submit-${tech.id}`}
              className="w-full py-2.5 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-sm font-bold transition disabled:opacity-40"
            >
              {quizBusy ? "Correction..." : "Valider mes réponses"}
            </button>
          ) : (
            <div className="text-center space-y-2" data-testid={`quiz-result-${tech.id}`}>
              <div className={`text-lg font-bold ${quizResult.passed ? "text-emerald-400" : "text-amber-400"}`}>
                {quizResult.passed ? "🎉 Réussi !" : "Continue !"} {quizResult.score}/{quizResult.total}
              </div>
              <button
                onClick={() => { setQuizResult(null); setQuizAns({}); }}
                className="text-xs px-3 py-1.5 rounded-md bg-zinc-800 hover:bg-zinc-700 text-zinc-200"
              >
                Recommencer
              </button>
            </div>
          )}
        </div>
      )}

      {open && (
        <div className="mt-3 pt-3 border-t border-zinc-800 space-y-3" data-testid={`tech-eval-${tech.id}`}>
          <div className="text-[11px] text-zinc-400 uppercase tracking-wider">
            {isKid ? "Comment tu te sens ?" : "Où en es-tu sur cette technique ?"}
          </div>
          <div className={`grid ${isKid ? "grid-cols-2" : "grid-cols-5"} gap-2`}>
            {options.map((opt) => (
              <button
                key={opt.v}
                disabled={busy}
                onClick={() => submit(opt.v, false)}
                className={`px-2 py-2 rounded-md text-[11px] font-medium transition disabled:opacity-50 ${
                  isKid
                    ? "bg-zinc-800 hover:bg-amber-900/70 border border-zinc-700 text-zinc-100"
                    : opt.color
                } ${localPct === opt.v ? "ring-2 ring-amber-400" : ""}`}
                data-testid={`btn-pct-${tech.id}-${opt.v}`}
              >
                {isKid ? (
                  <>
                    <div className="text-lg">{opt.emoji}</div>
                    <div className="text-[10px] mt-0.5">{opt.label}</div>
                  </>
                ) : (
                  <>
                    <div className="font-bold">{opt.v}%</div>
                    <div className="text-[9px] mt-0.5 opacity-90">{opt.label}</div>
                  </>
                )}
              </button>
            ))}
          </div>
          <button
            disabled={busy}
            onClick={() => submit(localPct || 75, true)}
            className="w-full px-3 py-2 rounded-md bg-gradient-to-r from-amber-700 to-amber-600 hover:from-amber-600 hover:to-amber-500 text-zinc-950 font-semibold text-xs transition disabled:opacity-50"
            data-testid={`btn-request-validation-${tech.id}`}
          >
            🙋 Demander à Kru de me valider sur cette technique
          </button>
        </div>
      )}
      {videoOpen && (
        <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4" onClick={() => setVideoOpen(false)} data-testid={`video-modal-${tech.id}`}>
          <div className="w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-white text-sm font-semibold truncate">{tech.name}</span>
              <button onClick={() => setVideoOpen(false)} data-testid={`video-close-${tech.id}`} className="text-white/60 hover:text-white text-xl leading-none px-2">×</button>
            </div>
            <div className="relative w-full rounded-lg overflow-hidden border border-zinc-700" style={{ paddingBottom: "56.25%" }}>
              <iframe title={tech.name} src={embedUrl} className="absolute inset-0 w-full h-full" allow="autoplay; fullscreen; picture-in-picture; encrypted-media" allowFullScreen />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CoachNote({ token }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const send = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/api/public/carnet/${token}/note`, { note });
      setSent(true);
      setNote("");
      setTimeout(() => setSent(false), 4000);
    } catch (e) {
      alert("Erreur : " + (e.response?.data?.detail || "réessaie"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mx-5 mb-6 mt-2 p-4 rounded-lg bg-zinc-900/60 border border-amber-900/40" data-testid="coach-note-box">
      <div className="text-xs uppercase tracking-wider text-amber-500/80 mb-2">📝 Message au Kru</div>
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Question, blessure, observation... (1000 caractères max)"
        rows={3}
        maxLength={1000}
        className="w-full bg-zinc-950 border border-zinc-800 rounded-md p-2 text-sm text-zinc-100 placeholder-zinc-600 focus:border-amber-700 focus:outline-none"
        data-testid="coach-note-input"
      />
      <button
        onClick={send}
        disabled={busy || !note.trim()}
        className="mt-2 w-full px-3 py-2 rounded-md bg-amber-800 hover:bg-amber-700 text-amber-50 text-xs font-semibold disabled:opacity-40 transition"
        data-testid="btn-send-note"
      >
        {busy ? "Envoi..." : "Envoyer à Kru Ralph"}
      </button>
      {sent && <div className="mt-2 text-xs text-emerald-400 text-center">✓ Message envoyé</div>}
    </div>
  );
}

export default function PublicCarnetView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/public/carnet/${token}`)
      .then(r => setData(r.data))
      .catch(e => setErr(e.response?.data?.detail || "Carnet introuvable ou lien expiré"));
  }, [token]);

  if (err) return (
    <div className="min-h-screen bg-zinc-950 text-zinc-200 flex items-center justify-center p-6" data-testid="public-carnet-error">
      <div className="text-center">
        <div className="text-2xl text-amber-400 mb-2">⛓️</div>
        <div className="text-lg">{err}</div>
      </div>
    </div>
  );
  if (!data) return (
    <div className="min-h-screen bg-zinc-950 text-zinc-400 flex items-center justify-center" data-testid="public-carnet-loading">
      Chargement du carnet…
    </div>
  );

  const { student, summary, techniques, branding } = data;
  const isKid = (student.age || 99) <= 7;
  const ageGroupLabel = {
    "3_7": "Petit Samouraï (3-6 ans)",
    "8_12": "Jeune (8-12 ans)",
    "13_15": "Ado (13-15 ans)",
    "16_plus": "Adulte (16+)",
  }[student.age_group] || "";

  // Group techniques by category
  const byCat = techniques.reduce((acc, t) => {
    const k = t.category || "Autre";
    acc[k] = acc[k] || [];
    acc[k].push(t);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-950 via-zinc-900 to-black text-zinc-100 pb-12" data-testid="public-carnet-view">
      {/* Header */}
      <div className="px-5 pt-8 pb-6 border-b border-amber-900/30 bg-gradient-to-r from-amber-950/20 to-zinc-900/20">
        <div className="text-xs uppercase tracking-widest text-amber-500/80">{branding?.school}</div>
        <div className="text-[10px] text-zinc-500 mb-3">{branding?.tagline}</div>
        <h1 className="text-2xl font-bold text-amber-400" data-testid="student-name">{student.name}</h1>
        <div className="mt-1 text-sm text-zinc-400">
          {ageGroupLabel} · {student.discipline?.replace("_", " ").toUpperCase()}
        </div>
        <div className="mt-2 flex items-center gap-2 text-sm" data-testid="belt-summary">
          <span className="px-2 py-0.5 rounded-full bg-zinc-800 border border-zinc-700 inline-flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full border border-white/30 shrink-0" style={{ background: publicBeltHex(student.current_belt) }} data-testid="belt-pastille-current" />
            Actuelle : <b>{student.current_belt}</b>
          </span>
          <span className="text-zinc-500">→</span>
          <span className="px-2 py-0.5 rounded-full bg-amber-950/60 border border-amber-800 text-amber-200 inline-flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full border border-white/30 shrink-0" style={{ background: publicBeltHex(student.target_belt) }} data-testid="belt-pastille-target" />
            Cible : <b>{student.target_belt}</b>
          </span>
        </div>
        {student.objective_type === "competitif" && (
          <span className="inline-block mt-2 px-2 py-0.5 rounded-full bg-red-900/60 border border-red-700 text-red-200 text-xs">Compétitif</span>
        )}
      </div>

      {/* Action banner */}
      <div className="mx-5 mt-4 p-3 rounded-lg bg-gradient-to-r from-amber-950/40 to-amber-900/20 border border-amber-700/40" data-testid="action-banner">
        <div className="text-sm font-semibold text-amber-300">🥋 Auto-évaluation active</div>
        <div className="text-xs text-zinc-300 mt-1">
          Clique sur <b>"M'auto-évaluer"</b> sur chaque technique pour me dire où tu en es. Kru validera au tatami.
        </div>
      </div>

      {/* Summary */}
      <div className="px-5 py-5 grid grid-cols-2 gap-3">
        <div className="rounded-lg bg-zinc-900/60 border border-zinc-800 p-3" data-testid="techniques-count">
          <div className="text-2xl font-bold text-amber-300">{summary.techniques_count}</div>
          <div className="text-xs text-zinc-500 uppercase">Techniques suivies</div>
        </div>
        <div className="rounded-lg bg-zinc-900/60 border border-zinc-800 p-3" data-testid="global-average">
          {summary.show_percent ? (
            <>
              <div className="text-2xl font-bold text-emerald-400">{summary.global_average}%</div>
              <div className="text-xs text-zinc-500 uppercase">Moyenne</div>
            </>
          ) : (
            <>
              <div className="text-2xl">⭐️⭐️⭐️</div>
              <div className="text-xs text-zinc-500 uppercase">Progression</div>
            </>
          )}
        </div>
      </div>

      {/* Status breakdown */}
      <div className="px-5 pb-3 flex flex-wrap gap-2 text-xs">
        {Object.entries(summary.by_status || {}).map(([s, c]) => (
          <span key={s} className={`px-2 py-1 rounded-full ${STATUS_COLOR[s] || "bg-zinc-800"}`} data-testid={`status-${s}`}>
            {STATUS_LABEL[s] || s} · {c}
          </span>
        ))}
      </div>

      {/* Progression par ceinture (barre + badge « débloquée ») */}
      {summary.show_percent && (() => {
        const beltOrder = ["Blanche", "Blanc/Jaune", "Blanc-Jaune", "Jaune", "Orange", "Rouge", "Vert", "Verte", "Bleu", "Bleue", "Mauve", "Brune", "Noire"];
        const beltHex = { Jaune: "#F5C518", Orange: "#FF8C1A", Vert: "#22C55E", Verte: "#22C55E", Bleu: "#3B82F6", Bleue: "#3B82F6", Rouge: "#EF4444", Mauve: "#A855F7", Brune: "#92400E", Noire: "#111827", Blanche: "#E5E7EB" };
        const byBelt = {};
        techniques.forEach((t) => {
          const b = t.belt_required || "Autre";
          (byBelt[b] = byBelt[b] || []).push(t);
        });
        const rows = Object.entries(byBelt).map(([belt, ts]) => {
          const avg = Math.round(ts.reduce((s, t) => s + (t.percent || 0), 0) / ts.length);
          const mastered = ts.filter((t) => t.status === "maitrise").length;
          return { belt, avg, count: ts.length, mastered, unlocked: mastered === ts.length && ts.length > 0 };
        }).sort((a, b) => {
          const ia = beltOrder.indexOf(a.belt), ib = beltOrder.indexOf(b.belt);
          return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
        });
        return (
          <div className="px-5 pb-4" data-testid="belt-progression">
            <div className="text-xs uppercase tracking-widest text-amber-500/80 mb-2">Progression par ceinture</div>
            <div className="space-y-2.5">
              {rows.map((r) => (
                <div key={r.belt} data-testid={`belt-row-${r.belt}`}>
                  <div className="flex items-center gap-2 mb-1 text-sm">
                    <span className="w-3 h-3 rounded-full border border-white/20 shrink-0" style={{ background: beltHex[r.belt] || "#666" }} />
                    <span className="font-medium">{r.belt}</span>
                    <span className="text-zinc-500 text-xs">{r.mastered}/{r.count}</span>
                    {r.unlocked && (
                      <span className="ml-auto text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/40" data-testid={`belt-unlocked-${r.belt}`}>
                        🏅 Débloquée
                      </span>
                    )}
                    {!r.unlocked && <span className="ml-auto text-xs text-zinc-400">{r.avg}%</span>}
                  </div>
                  <div className="h-2.5 rounded-full bg-zinc-800 overflow-hidden">
                    <div className="h-full rounded-full transition-all"
                      style={{ width: `${r.avg}%`, background: r.unlocked ? "#10b981" : (beltHex[r.belt] || "#F5C518") }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}


      {/* Techniques */}
      <div className="px-5 pt-2 space-y-5">
        {Object.entries(byCat).map(([cat, items]) => (
          <div key={cat} data-testid={`category-${cat}`}>
            <div className="text-xs uppercase tracking-wider text-amber-600/80 mb-2 border-b border-zinc-800 pb-1">{cat}</div>
            <div className="space-y-2">
              {items.map((t) => (
                <TechniqueRow
                  key={t.id}
                  tech={t}
                  token={token}
                  showPercent={summary.show_percent}
                  isKid={isKid}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Coach note box */}
      <CoachNote token={token} />

      <div className="px-5 py-6 text-center text-[10px] text-zinc-600 border-t border-zinc-900">
        © Institut Impact Élite Martial · Kru Ralph D. Jean-Pierre
      </div>
    </div>
  );
}
